import React from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'shared/components';
import { AITextareaField } from './AITextareaField';
import { ProposalAIPrimaryButton } from './ProposalAIPrimaryButton';

interface Props {
  prompt: string;
  generated: GenerateAdditionalInfoSuggestionPayload | null;
  isGenerating: boolean;
  isGeneratedStale: boolean;
  onPromptChange: (value: string) => void;
  onGenerate: React.MouseEventHandler<HTMLButtonElement>;
  onApply: React.MouseEventHandler<HTMLButtonElement>;
  onCancel: React.MouseEventHandler<HTMLButtonElement>;
}

export const AdditionalInfoAIPanel: React.FC<Props> = ({
  prompt,
  generated,
  isGenerating,
  isGeneratedStale,
  onPromptChange,
  onGenerate,
  onApply,
  onCancel,
}) => {
  const { formatMessage } = useIntl();

  return (
    <div className="proposal-additional-info-ai__panel">
      <div className="proposal-additional-info-ai__header">
        <p className="t-label m-bottom--tny">
          {formatMessage({ id: 'proposal-ai.additional-info.eyebrow' })}
        </p>
        <p className="t-text-2 m-bottom-0">
          {formatMessage({ id: 'proposal-ai.additional-info.description' })}
        </p>
      </div>

      <AITextareaField
        id="proposal-ai-additional-info-prompt"
        label={formatMessage({
          id: 'proposal-ai.additional-info.prompt.label',
        })}
        placeholder={formatMessage({
          id: 'proposal-ai.additional-info.prompt.placeholder',
        })}
        value={prompt}
        onChange={onPromptChange}
        dataCy="proposal-ai-additional-info-prompt"
        textareaClassName="proposal-additional-info-ai__prompt"
      />

      <div className="proposal-ai-generator__action-row">
        <ProposalAIPrimaryButton
          label={
            isGenerating
              ? formatMessage({
                  id: 'proposal-ai.additional-info.generate.loading',
                })
              : generated
                ? formatMessage({
                    id: 'proposal-ai.additional-info.generate.regenerate',
                  })
                : formatMessage({
                    id: 'proposal-ai.additional-info.generate.action',
                  })
          }
          onClick={onGenerate}
          disabled={isGenerating}
          btnSelector="proposal-ai-additional-info-generate"
          size="med"
        />
        <Button
          style="ghost"
          size="med"
          onClick={onCancel}
          btnSelector="proposal-ai-additional-info-cancel"
        >
          {formatMessage({ id: 'buttons.cancel' })}
        </Button>
      </div>

      {generated && (
        <>
          {isGeneratedStale && (
            <div className="proposal-ai-generator__notice proposal-ai-generator__notice--warning">
              <p className="t-text-2 m-bottom-0">
                {formatMessage({
                  id: 'proposal-ai.additional-info.preview.stale',
                })}
              </p>
            </div>
          )}

          <div className="proposal-additional-info-ai__preview">
            <p className="t-label m-bottom--tny">
              {formatMessage({
                id: 'proposal-ai.additional-info.preview.title',
              })}
            </p>
            <p className="t-text-2 m-bottom-0 proposal-additional-info-ai__preview-text">
              {generated.additionalInfo}
            </p>
          </div>

          <div className="proposal-ai-generator__action-row">
            <ProposalAIPrimaryButton
              label={formatMessage({
                id: 'proposal-ai.additional-info.apply',
              })}
              onClick={onApply}
              disabled={isGeneratedStale}
              btnSelector="proposal-ai-additional-info-apply"
              size="med"
            />
          </div>
        </>
      )}
    </div>
  );
};
